import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

// FastAPI 422 returns array of objects; normalise everything to string.
export function formatApiError(err) {
    const detail = err?.response?.data?.detail;
    if (detail == null) return err?.message || "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export async function apiLogin(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    return data; // { user, access_token, must_change_password }
}

export async function apiLogout() {
    await api.post("/auth/logout");
}

export async function apiMe() {
    const { data } = await api.get("/auth/me");
    return data;
}

export async function apiChangePassword(current_password, new_password) {
    const { data } = await api.post("/auth/change-password", {
        current_password,
        new_password,
    });
    return data;
}

export async function apiCreateInvite(email, full_name, role) {
    const { data } = await api.post("/admin/invites", { email, full_name, role });
    return data; // { user, invite_token, invite_expires_in_days }
}

export async function apiGetInvite(token) {
    const { data } = await api.get(`/auth/invite/${encodeURIComponent(token)}`);
    return data;
}

export async function apiAcceptInvite(token, password) {
    const { data } = await api.post("/auth/accept-invite", { token, password });
    return data;
}

// ---- Admin ----
export async function apiListUsers() {
    const { data } = await api.get("/users");
    return data;
}

export async function apiUpdateUser(userId, patch) {
    const { data } = await api.patch(`/users/${userId}`, patch);
    return data;
}

export async function apiResendInvite(userId) {
    const { data } = await api.post(`/admin/invites/${userId}/resend`);
    return data;
}

export async function apiRevokeInvite(userId) {
    await api.delete(`/admin/invites/${userId}`);
}

export async function apiAdminResetPassword(userId, new_password, force_change_on_next_login = true) {
    const { data } = await api.post(`/users/${userId}/reset-password`, {
        new_password,
        force_change_on_next_login,
    });
    return data;
}

// ---- Branding ----
export function apiBrandingLogoUrl(stamp = "") {
    const s = stamp ? `?t=${encodeURIComponent(stamp)}` : "";
    return `${API}/branding/logo${s}`;
}
export async function apiUploadBrandingLogo(file) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/branding/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
}
export async function apiRemoveBrandingLogo() {
    await api.delete("/branding/logo");
}

// ---- Reports ----
export async function apiReportTimesheet({ from_date, to_date, project_id } = {}) {
    const p = new URLSearchParams({ from_date, to_date });
    if (project_id) p.set("project_id", project_id);
    const { data } = await api.get(`/reports/timesheet?${p.toString()}`);
    return data;
}
export async function apiReportLeaves({ from_date, to_date, project_id } = {}) {
    const p = new URLSearchParams({ from_date, to_date });
    if (project_id) p.set("project_id", project_id);
    const { data } = await api.get(`/reports/leaves?${p.toString()}`);
    return data;
}
export async function apiReportRoster({ from_date, to_date, project_id } = {}) {
    const p = new URLSearchParams({ from_date, to_date });
    if (project_id) p.set("project_id", project_id);
    const { data } = await api.get(`/reports/roster?${p.toString()}`);
    return data;
}
export async function apiReportHandovers({ from_date, to_date, project_id, include_items } = {}) {
    const p = new URLSearchParams({ from_date, to_date });
    if (project_id) p.set("project_id", project_id);
    if (include_items) p.set("include_items", "true");
    const { data } = await api.get(`/reports/handovers?${p.toString()}`);
    return data;
}
export async function apiTimesheetOverride(assignmentId, patch) {
    const { data } = await api.patch(`/assignments/${assignmentId}/timesheet-override`, patch);
    return data;
}
export function apiReportExportUrl(kind, { format, from_date, to_date, project_id, include_items }) {
    const p = new URLSearchParams({ format, from_date, to_date });
    if (project_id) p.set("project_id", project_id);
    if (include_items) p.set("include_items", "true");
    return `${API}/reports/${kind}/export?${p.toString()}`;
}

export async function apiListSkills() {
    const { data } = await api.get("/skills");
    return data.skills;
}

export async function apiAddSkill(name) {
    const { data } = await api.post("/skills", { name });
    return data.skills;
}

export async function apiListProjects() {
    const { data } = await api.get("/projects");
    return data;
}

export async function apiCreateProject(payload) {
    // payload = { name, code, customer_id, description?, status?, start_date?, project_manager_id?, distribution_emails? }
    const { data } = await api.post("/projects", payload);
    return data;
}

export async function apiUpdateProject(projectId, patch) {
    const { data } = await api.patch(`/projects/${projectId}`, patch);
    return data;
}

export async function apiDeleteProject(projectId) {
    await api.delete(`/projects/${projectId}`);
}

// ---- Customers ----
export async function apiListCustomers() {
    const { data } = await api.get("/customers");
    return data;
}

export async function apiCreateCustomer(payload) {
    const { data } = await api.post("/customers", payload);
    return data;
}

export async function apiUpdateCustomer(customerId, patch) {
    const { data } = await api.patch(`/customers/${customerId}`, patch);
    return data;
}

export async function apiDeleteCustomer(customerId) {
    await api.delete(`/customers/${customerId}`);
}

// ---- Project Tasks (v0.13) ----
export async function apiListTasks(projectId, params = {}) {
    const q = new URLSearchParams(params).toString();
    const url = `/projects/${projectId}/tasks` + (q ? `?${q}` : "");
    const { data } = await api.get(url);
    return data;
}

export async function apiTasksCalendar(projectId, fromDate, toDate) {
    const { data } = await api.get(
        `/projects/${projectId}/tasks/calendar?from_date=${fromDate}&to_date=${toDate}`,
    );
    return data;
}

export async function apiCreateTask(projectId, payload) {
    const { data } = await api.post(`/projects/${projectId}/tasks`, payload);
    return data;
}

export async function apiUpdateTask(projectId, taskId, patch) {
    const { data } = await api.patch(`/projects/${projectId}/tasks/${taskId}`, patch);
    return data;
}

export async function apiUpdateTaskStatus(projectId, taskId, status) {
    const { data } = await api.patch(
        `/projects/${projectId}/tasks/${taskId}/status`,
        { status },
    );
    return data;
}

export async function apiSendTaskReminderNow(projectId, taskId) {
    const { data } = await api.post(
        `/projects/${projectId}/tasks/${taskId}/send-reminder-now`,
    );
    return data;
}

export async function apiDeleteTask(projectId, taskId) {
    await api.delete(`/projects/${projectId}/tasks/${taskId}`);
}

export async function apiGetTaskReminderEmails(projectId) {
    const { data } = await api.get(`/projects/${projectId}/task-reminder-emails`);
    return data.task_reminder_emails || [];
}

export async function apiSetTaskReminderEmails(projectId, emails) {
    const { data } = await api.patch(`/projects/${projectId}/task-reminder-emails`, {
        task_reminder_emails: emails,
    });
    return data.task_reminder_emails || [];
}

export async function apiListShifts(projectId) {
    const url = projectId ? `/shifts?project_id=${encodeURIComponent(projectId)}` : "/shifts";
    const { data } = await api.get(url);
    return data;
}

export async function apiCreateShift(project_id, name, start_time, end_time) {
    const { data } = await api.post("/shifts", { project_id, name, start_time, end_time });
    return data;
}

export async function apiUpdateShift(shiftId, patch) {
    const { data } = await api.patch(`/shifts/${shiftId}`, patch);
    return data;
}

export async function apiDeleteShift(shiftId) {
    await api.delete(`/shifts/${shiftId}`);
}

// ---- Roster ----
export async function apiListAssignments({ project_id, from_date, to_date } = {}) {
    const params = new URLSearchParams();
    if (project_id) params.set("project_id", project_id);
    if (from_date) params.set("from_date", from_date);
    if (to_date) params.set("to_date", to_date);
    const { data } = await api.get(`/assignments?${params.toString()}`);
    return data;
}

export async function apiCreateAssignment(payload) {
    const { data } = await api.post("/assignments", payload);
    return data; // { assignment, warning }
}

export async function apiUpdateAssignment(assignmentId, patch) {
    const { data } = await api.patch(`/assignments/${assignmentId}`, patch);
    return data;
}

export async function apiDeleteAssignment(assignmentId) {
    await api.delete(`/assignments/${assignmentId}`);
}

export async function apiCopyAssignments(payload) {
    // payload = { project_id, source_from_date, source_to_date, shift_ids?, target_week_starts: [] }
    const { data } = await api.post("/assignments/copy", payload);
    return data;
}

export async function apiAssignableUsers(project_id) {
    const { data } = await api.get(
        `/users/assignable?project_id=${encodeURIComponent(project_id)}`,
    );
    return data;
}

// ---- Leaves ----
export async function apiListLeaves({ user_id, status, from_date, to_date } = {}) {
    const params = new URLSearchParams();
    if (user_id) params.set("user_id", user_id);
    if (status) params.set("status", status);
    if (from_date) params.set("from_date", from_date);
    if (to_date) params.set("to_date", to_date);
    const { data } = await api.get(`/leaves?${params.toString()}`);
    return data;
}

export async function apiCreateLeave(payload) {
    const { data } = await api.post("/leaves", payload);
    return data;
}

export async function apiUpdateLeaveStatus(leaveId, status) {
    const { data } = await api.patch(`/leaves/${leaveId}`, { status });
    return data;
}

export async function apiCancelLeave(leaveId) {
    await api.delete(`/leaves/${leaveId}`);
}

// ---- Handovers ----
export async function apiListHandovers({ project_id, from_date, to_date, status, mine } = {}) {
    const p = new URLSearchParams();
    if (project_id) p.set("project_id", project_id);
    if (from_date) p.set("from_date", from_date);
    if (to_date) p.set("to_date", to_date);
    if (status) p.set("status", status);
    if (mine) p.set("mine", "true");
    const { data } = await api.get(`/handovers?${p.toString()}`);
    return data;
}

export async function apiCreateHandover(payload) {
    const { data } = await api.post("/handovers", payload);
    return data;
}

export async function apiGetHandover(id) {
    const { data } = await api.get(`/handovers/${id}`);
    return data; // { handover, items, can_edit }
}

export async function apiPatchHandover(id, patch) {
    const { data } = await api.patch(`/handovers/${id}`, patch);
    return data;
}

export async function apiSubmitHandover(id) {
    const { data } = await api.post(`/handovers/${id}/submit`);
    return data;
}

export async function apiAcknowledgeHandover(id) {
    const { data } = await api.post(`/handovers/${id}/acknowledge`);
    return data;
}

export async function apiCarryForwardHandover(id, target_date) {
    const payload = target_date ? { target_date } : {};
    const { data } = await api.post(`/handovers/${id}/carry-forward`, payload);
    return data; // { handover, copied, skipped, created_new_handover }
}

export async function apiDeleteHandover(id) {
    await api.delete(`/handovers/${id}`);
}

export async function apiAddItem(handoverId, payload) {
    const { data } = await api.post(`/handovers/${handoverId}/items`, payload);
    return data;
}

export async function apiPatchItem(handoverId, itemId, patch) {
    const { data } = await api.patch(`/handovers/${handoverId}/items/${itemId}`, patch);
    return data;
}

export async function apiDeleteItem(handoverId, itemId) {
    await api.delete(`/handovers/${handoverId}/items/${itemId}`);
}

export async function apiSearchHandovers(q, project_id) {
    const p = new URLSearchParams({ q });
    if (project_id) p.set("project_id", project_id);
    const { data } = await api.get(`/handover-search?${p.toString()}`);
    return data;
}

export async function apiIncidentTrail(reference) {
    const { data } = await api.get(
        `/handover-trail?reference=${encodeURIComponent(reference)}`,
    );
    return data;
}

export async function apiRunDigest(for_date) {
    const url = for_date ? `/digests/run?for_date=${for_date}` : "/digests/run";
    const { data } = await api.post(url);
    return data;
}

export async function apiDigestLog() {
    const { data } = await api.get("/digests/log");
    return data;
}

export async function apiDashboard() {
    const { data } = await api.get("/dashboard/summary");
    return data;
}

export async function apiSendHandoverEmail(handoverId, payload) {
    const { data } = await api.post(`/handovers/${handoverId}/send-email`, payload);
    return data;
}

export async function apiGetHandoverEmailLog(handoverId) {
    const { data } = await api.get(`/handovers/${handoverId}/email-log`);
    return data.log;
}
